import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { dashApi, type AssuranceResult, type ConnectorOp, type SloConnector } from "@/lib/dashApi";
import { EmptyState, ErrorState, LoadingState, PageHeader, Panel, Stat } from "@/components/dash/ui";

const CLASS_TONE: Record<string, string> = {
  healthy: "text-emerald-300", capturing_degraded: "text-sky-300", stale: "text-amber-300",
  seeded_only: "text-amber-300", no_live_capture: "text-rose-300", source_unavailable: "text-rose-300",
  not_configured: "text-slate-500",
};
const PROV_TONE: Record<string, string> = {
  verified_capture: "text-emerald-300", mixed_verified_and_seeded: "text-sky-300",
  seeded_or_legacy_only: "text-amber-300", none: "text-slate-500",
};

export default function ConnectorOperationsPage() {
  const [tab, setTab] = useState<"ops" | "assurance">("ops");
  return (
    <div className="space-y-6">
      <PageHeader
        title="Connector Operations & Verified Capture"
        description="Honest connector state. A degraded/partial run is a verified live capture WITH informational caveats — not a failure. Pre-run-history rows are labelled seeded/legacy and never presented as verified live capture. IRONDash reads sources read-only; no source error payloads, URLs or credentials are shown."
      />
      <div className="flex gap-2 text-sm">
        <button onClick={() => setTab("ops")} className={`rounded px-3 py-1 ${tab === "ops" ? "bg-sky-500/20 text-sky-200" : "bg-slate-800 text-slate-400"}`}>Operations</button>
        <button onClick={() => setTab("assurance")} className={`rounded px-3 py-1 ${tab === "assurance" ? "bg-sky-500/20 text-sky-200" : "bg-slate-800 text-slate-400"}`}>Assurance & SLOs</button>
      </div>
      {tab === "ops" ? <OpsTab /> : <AssuranceTab />}
    </div>
  );
}

function OpsTab() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["connector-ops"], queryFn: dashApi.connectorOperations });
  if (q.isLoading) return <LoadingState />;
  if (q.isError) return <ErrorState error={q.error} />;
  const s = q.data!.summary as Record<string, number>;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
        <Stat label="Capturing live" value={s.capturing_live} />
        <Stat label="Degraded (with caveats)" value={s.degraded} />
        <Stat label="Genuinely failed" value={s.failed} sub={s.failed ? "needs attention" : undefined} />
        <Stat label="Seeded-only" value={s.seeded_only} />
        <Stat label="Never verified" value={s.never_verified} />
      </div>
      <div className="space-y-2">
        {q.data!.connectors.map((c) => <ConnectorCard key={c.source} c={c} onRun={() => qc.invalidateQueries({ queryKey: ["connector-ops"] })} />)}
      </div>
      <p className="text-[11px] text-slate-600">{(q.data!.summary as Record<string, string>).no_causation}</p>
    </div>
  );
}

const SLO_TONE: Record<string, string> = {
  meeting: "text-emerald-300", warning: "text-amber-300", breached: "text-rose-300",
  insufficient_history: "text-slate-400", not_applicable: "text-slate-500", blocked: "text-rose-300", disabled: "text-slate-500",
};

function AssuranceTab() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["connector-assurance"], queryFn: dashApi.connectorAssurance });
  const inv = () => qc.invalidateQueries({ queryKey: ["connector-assurance"] });
  if (q.isLoading) return <LoadingState />;
  if (q.isError) return <ErrorState error={q.error} />;
  const a = q.data as AssuranceResult;
  const s = a.summary;
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
        <Stat label="Meeting" value={s.meeting} />
        <Stat label="Warning" value={s.warning} />
        <Stat label="Breached" value={s.breached} sub={s.breached ? "needs attention" : undefined} />
        <Stat label="Insufficient history" value={s.insufficient_history} />
        <Stat label="Blocked / N/A" value={s.blocked} />
      </div>
      {a.open_incidents.length > 0 && (
        <Panel title={`Open reliability incidents (${a.open_incidents.length})`}>
          <div className="space-y-1 text-xs">
            {a.open_incidents.map((i) => (
              <div key={i.id} className="flex flex-wrap items-center gap-2">
                <span className={i.severity === "critical" ? "text-rose-300" : "text-amber-300"}>{i.status}</span>
                <span className="text-slate-200">{i.source_code}: {i.title}</span>
                <button onClick={() => dashApi.incidentResolve(i.id, { by_verified_capture: true }).then(inv).catch(() => alert("Cannot resolve — connector is not currently capturing."))}
                  className="ml-auto rounded bg-emerald-500/15 px-2 py-0.5 text-emerald-300">resolve (verified)</button>
              </div>
            ))}
          </div>
        </Panel>
      )}
      <Panel title="Connector SLO state">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr className="text-left text-[10px] uppercase text-slate-500">
              <th className="pb-1 pr-3">Connector</th><th className="pb-1 pr-3">SLO state</th><th className="pb-1 pr-3">Capture</th>
              <th className="pb-1 pr-3">Freshness</th><th className="pb-1 pr-3">Runs (win)</th><th className="pb-1 pr-3">Capture rate</th><th className="pb-1">Action</th>
            </tr></thead>
            <tbody>
              {a.connectors.map((c: SloConnector) => (
                <tr key={c.source} className="border-t border-slate-800">
                  <td className="py-1.5 pr-3 text-slate-200">{c.label}{c.criticality && <span className="ml-1 text-[10px] text-slate-500">{c.criticality}</span>}</td>
                  <td className={`py-1.5 pr-3 ${SLO_TONE[c.state] ?? "text-slate-400"}`}>{c.state}</td>
                  <td className="py-1.5 pr-3 text-slate-500">{c.capture_class}</td>
                  <td className="py-1.5 pr-3 text-slate-400">{c.freshness_minutes != null ? `${c.freshness_minutes}m` : "—"}</td>
                  <td className="py-1.5 pr-3 text-slate-400">{c.capture_runs}/{c.runs_in_window}</td>
                  <td className="py-1.5 pr-3 text-slate-400">{c.rates.verified_capture_rate_pct != null ? `${c.rates.verified_capture_rate_pct}%` : "withheld"}</td>
                  <td className="py-1.5">
                    {c.state === "breached" && !c.has_open_incident && (
                      <button onClick={() => dashApi.openIncident({ source_code: c.source, title: `${c.label} SLO breached`, severity: "warning" }).then(inv)}
                        className="rounded bg-slate-700 px-2 py-0.5 text-slate-300">open incident</button>)}
                    {c.has_open_incident && <span className="text-[10px] text-amber-400">incident open</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
      {a.connectors.length === 0 && <EmptyState>No connectors.</EmptyState>}
      <p className="text-[11px] text-slate-600">{a.no_causation}</p>
    </div>
  );
}

function ConnectorCard({ c, onRun }: { c: ConnectorOp; onRun: () => void }) {
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const run = useMutation({
    mutationFn: () => dashApi.runConnector(c.source),
    onSuccess: (d) => { setMsg(d.note); onRun(); },
    onError: (e) => setMsg(e instanceof Error ? e.message : "failed"),
  });
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/30 p-3 text-xs">
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={() => setOpen((o) => !o)} className="text-slate-200">{open ? "▾" : "▸"} {c.label}</button>
        <span className={`rounded bg-slate-800 px-1.5 py-0.5 text-[10px] ${CLASS_TONE[c.capture_class] ?? "text-slate-400"}`}>{c.capture_class}</span>
        <span className={`text-[10px] ${PROV_TONE[c.provenance] ?? "text-slate-500"}`}>provenance: {c.provenance}</span>
        {c.latest_verified_live_capture && <span className="text-[10px] text-slate-500">verified capture {c.latest_verified_live_capture.slice(0, 10)}</span>}
        <span className="ml-auto text-[10px] text-slate-500">verified/seeded {c.verified_dates}/{c.seeded_dates}</span>
        <button onClick={() => run.mutate()} disabled={run.isPending} className="rounded bg-slate-700 px-2 py-0.5 text-slate-300 disabled:opacity-40">run capture</button>
      </div>
      {msg && <p className="mt-1 text-sky-300">{msg}</p>}
      {open && (
        <div className="mt-2 space-y-2 border-t border-slate-800 pt-2">
          <dl className="grid grid-cols-2 gap-x-4 gap-y-1 sm:grid-cols-3">
            <F k="Health" v={c.health_status} />
            <F k="Latest run" v={`${c.latest_run_status ?? "—"} ${c.latest_run_at?.slice(0, 16).replace("T", " ") ?? ""}`} />
            <F k="Latest clean success" v={c.latest_success_at?.slice(0, 16).replace("T", " ") ?? "never"} />
            <F k="Latest snapshot" v={`${c.latest_snapshot_status ?? "—"} ${c.latest_snapshot_at?.slice(0, 10) ?? ""}`} />
            <F k="Latest warehouse date" v={c.latest_warehouse_date ?? "—"} />
            <F k="Seeded-only" v={c.seeded_or_legacy_data_only ? "yes" : "no"} />
          </dl>
          <div className="flex flex-wrap gap-3 text-[10px]">
            <span className={c.successful_run ? "text-emerald-300" : "text-slate-500"}>✓ run</span>
            <span className={c.successful_snapshot ? "text-emerald-300" : "text-slate-500"}>✓ snapshot</span>
            <span className={c.successful_warehouse_capture ? "text-emerald-300" : "text-slate-500"}>✓ warehouse capture</span>
          </div>
          {c.impact.length > 0 && <p className="text-slate-500">Affects: {c.impact.join(", ")}</p>}
          {c.failure_note && <p className="text-rose-300">Failure: {c.failure_note}</p>}
          {c.caveats.length > 0 && <ul className="space-y-0.5 text-slate-500">{c.caveats.map((cv, i) => <li key={i}>• {cv}</li>)}</ul>}
          <p className="text-slate-400">Recovery: {c.recovery}</p>
        </div>
      )}
    </div>
  );
}

function F({ k, v }: { k: string; v: string }) {
  return <div><dt className="text-slate-500">{k}</dt><dd className="text-slate-300">{v}</dd></div>;
}
